/**
 * 「這張進貨單是不是派工單」的判斷(REQ-20260818002)。
 *
 * 派工單(印刷派工 / 信封成型 / 偽卡成型 / 裁型代工…)與一般訂購單走同一張
 * `purchase_orders`,差別只在**要不要顯示 / 列印生產指示欄位**。
 *
 * 判斷依據是**進貨類別的名稱**,不是 code:
 *  - 類別自 REQ-20260814002 起由使用者在「設定 → 系統設定 → 進貨類別」自助新增,
 *    code 是使用者自己取的(`WORK_ORDER`? `PAIGONG`? `DG01`?)—— 寫死比對某個 code
 *    等於「使用者照需求新增了『裁型派工』,欄位卻不會出現」。
 *  - 也**不新增旗標欄位**到 reference 表:設定頁的 `SettingsTable` 是 14 張表共用的
 *    泛型元件,為了一個布林值改它會波及全部;REQ-20260814002 已經為同類理由
 *    (磅數星號綁 `PAPER`)決定不開這個欄位。
 *
 * 代價寫在這裡讓下一個人看得到:**使用者若把類別取成不含「派工」的名字**
 * (例如「代工單」),生產指示欄位就不會出現。這是刻意用「零設定」換來的 ——
 * 使用者只要照著紙本單據命名(紙本抬頭本來就叫「◯◯派工單」)就會對。
 */
export function isWorkOrderCategory(categoryName: string | undefined): boolean {
  return (categoryName ?? '').includes('派工');
}

/**
 * 「這張單的品項要不要填印刷顏色 / 加工」的判斷(REQ-20260821005)。
 *
 * 與 `isWorkOrderCategory()` **刻意分開**:REQ-20260818002 §七 記的那個風險真的發生了 ——
 * 使用者的外包類別叫「外包加工」,不含「派工」兩字,所以連印刷顏色 / 加工都看不到。
 * 但那組派工欄位群(用紙廠商 / 交件日期 / 取件方式 / 印工張數 / 成品尺寸 / 成品數量 /
 * 是否校色 / 特殊項目)是**派工單專屬的生產指示**,一般外包加工單不需要,
 * 所以合成同一個布林值會把使用者沒要的欄位一起打開。兩個判斷式互不影響。
 *
 * 關鍵字取「派工」「外包」「加工」——「紙張材料」「配件五金」這類純採購不含任一個,
 * 自然維持精簡表單。判斷依據仍是**名稱**不是 code,理由與上面完全相同。
 */
export function showsProcessingFields(categoryName: string | undefined): boolean {
  const name = categoryName ?? '';
  return ['派工', '外包', '加工'].some((keyword) => name.includes(keyword));
}

/**
 * 「這張單的品項要走**外包加工版面**嗎」的判斷(REQ-20260821006)。
 *
 * 第三個判斷式,而**不是**把上面的 `showsProcessingFields()` 拿來多用一次 ——
 * 這裡控制的是「紙張維度那一整列(磅數 / 尺寸 開數 / 紋路 / 換算率 / 庫存單位數量)
 * 要不要**藏起來**」,而派工單需要其中的「尺寸 / 開數」:
 * 那一格就是紙本派工單的**紙張尺寸**(REQ-20260818002 §二 對照表),
 * `WorkOrderSheet` 列印頁已經在讀它。用 `showsProcessingFields()`(含「派工」)
 * 來藏,等於連派工單的紙張尺寸一起藏掉,列印出來那一格會變空白。
 *
 * 所以這裡**明確排除派工類**:只有「外包加工」這種發包出去、公司不碰紙的類別
 * 才換版面。關鍵字仍是名稱不是 code,理由與上面兩個完全相同。
 */
export function isOutsourceProcessingCategory(categoryName: string | undefined): boolean {
  const name = categoryName ?? '';
  if (isWorkOrderCategory(name)) return false;
  return ['外包', '加工'].some((keyword) => name.includes(keyword));
}

/**
 * 「這張單的品項要走**商品採購版面**嗎」的判斷(REQ-20260904003)。
 *
 * 第四個判斷式。商品採購進的是**成品**(現成的商品)而不是紙 —— 紙張特有的
 * 紙張顏色 / 厚度 · 磅數 / 厚度單位 / 紋路 / 尺寸 · 開數 / 換算率整組都用不到,
 * 品項只需要品名 + 三格描述(規格 / 材質 / 加工)+ 數量 / 單位 / 單價 / 總價。
 *
 * ⚠️ **關鍵字只能是「商品」,絕對不能用「採購」。** REQ-20260814002 seed 進去的
 * 預設類別就叫**「紙張採購」** —— 用「採購」比對會把紙張材料單也一起換版面,
 * 而那是正式環境現有單據最多的類別,等於整批舊單一打開就看不到磅數與尺寸 / 開數。
 *
 * 先排除派工與外包加工:類別若取成「商品加工」之類同時命中兩組關鍵字,
 * **維持既有版面優先** —— 兩種版面互搶的結果是使用者看到的欄位隨命名順序而定,
 * 而既有版面是正在用的那一份。
 *
 * 判斷依據仍是**類別名稱**不是 code,也**不在 `purchase_categories` reference 表加
 * 旗標欄位**,理由與上面三個完全相同(設定頁的 `SettingsTable` 是 14 張表共用的
 * 泛型元件,為一個布林值改它會波及全部)。
 *
 * 零設定的代價一樣寫在這裡:**使用者若把類別取成不含「商品」的名字**
 * (例如「成品叫貨」),就不會換成這個版面 —— 退回既有的紙張版面,
 * 與改動前完全相同,不是壞掉。
 */
export function isGoodsPurchaseCategory(categoryName: string | undefined): boolean {
  const name = categoryName ?? '';
  if (isWorkOrderCategory(name)) return false;
  if (isOutsourceProcessingCategory(name)) return false;
  return name.includes('商品');
}

/**
 * 「這張進貨單是不是運輸費用單」的判斷(REQ-20260907002)。
 *
 * 第五個判斷式。運費單進的不是東西也不是工,是**一趟一趟的貨運班次** ——
 * 品項那一整套(品名 / 規格 / 紙張維度 / 數量單位 / 換算率)全都用不上,
 * 明細要的是 運送日 / 託運單號 / 送達客戶 / 付款 / 憑證 / 金額 / 歸屬工單。
 * 所以它換的不只是品項版面,是**整張表單的版面**(見 `PurchaseOrderForm`)。
 *
 * 關鍵字取「運費」「運輸」—— 正式環境的類別叫**「運輸費用」**
 * (code `FREIGHT`,REQ-20260829003 §1.2 查證過),而使用者自己新增時也可能取
 * 「運費」「貨運運費」之類。
 *
 * ⚠️ 判斷依據仍是**類別名稱不是 code**,也**不在 `purchase_categories` reference 表
 * 加旗標欄位** —— 理由與上面四個完全相同(設定頁的 `SettingsTable` 是 14 張表共用的
 * 泛型元件,為一個布林值改它會波及全部)。
 *
 * ⚠️ 這裡**不排除**前四個判斷式:名稱同時含「運輸」與「派工」的類別不存在於
 * 正式資料,而多一層互斥判斷只會讓五個判斷式的優先順序變成新的隱藏規則。
 * 真的撞到時運費版面優先(在 `PurchaseOrderForm` 裡是先判這一個),
 * 代價與前四個一樣寫在這裡讓下一個人看得到。
 *
 * 零設定的代價同上:**使用者若把類別取成不含「運費 / 運輸」的名字**
 * (例如「貨運行」),就不會換成運費版面 —— 退回既有的紙張版面,
 * 與改動前完全相同,不是壞掉。
 */
export function isFreightCategory(categoryName: string | undefined): boolean {
  const name = categoryName ?? '';
  return ['運費', '運輸'].some((keyword) => name.includes(keyword));
}
